<script setup lang="ts">
import {waitingListSchema, type WaitingListForm} from "~/schemas/WaitingList";
import MarqueeAlpha from "~/components/MarqueeAlpha.vue";
import HeroNotHome from "~/components/HeroNotHome.vue";

useWreadiaSeo({
  title: "Le club de la fiction indépendante",
});

const state = reactive({
  last_name: "",
  first_name: "",
  email: "",
  birth_date: "",
  socials: [""],
  links: [""],
  has_work: false,
  has_work_details: "",
  optin_tos: false as any,
  optin_data_processing: false as any,
  token: "",
  type: "author",
});

const turnstile = ref();
const loading = ref(false);
const submitted = ref(false);
const error = ref<string | null>(null);

const addSocial = () => {
  state.socials.push("");
};
const removeSocial = (index: number) => {
  state.socials.splice(index, 1);
};

const addLink = () => {
  state.links.push("");
};
const removeLink = (index: number) => {
  state.links.splice(index, 1);
};

async function onSubmit(event: any) {
  loading.value = true;
  error.value = null;
  try {
    const data = {
      ...state,
      socials: state.socials.filter(s => s && s.trim() !== ""),
      links: state.links.filter(w => w && w.trim() !== ""),
    };
    await $api("/api/waiting-list", {
      method: "POST",
      body: data,
    });
    submitted.value = true;
  } catch (err: any) {
    error.value = err.data?.message || "Une erreur est survenue lors de l'envoi de votre candidature.";
    turnstile.value?.reset();
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <UPage id="pagePage">
    <div id="pageBody" class="mt-0 bg-gray-100">

      <section class="m-0 border-b-2 border-primary bg-gradient-nugget pb-12 md:pb-0">
        <MarqueeAlpha></MarqueeAlpha>
        <HeroNotHome line1="Partager" line2="Être lu•e"></HeroNotHome>
      </section>

      <section class="m-0 py-8 md:py-16">
        <UContainer>
          <div class="text-center">
            <h2 class="mb-16">Parle-nous de toi et de ton travail !</h2>
            <UCard class="max-w-2xl mx-auto text-left" color="neutral" variant="outline">
              <div v-if="submitted" class="py-8 text-center">
                <UIcon name="i-custom-heart-red" class="size-16 text-success mx-auto mb-4"/>
                <h3 class="text-2xl font-bold mb-2">Enchanté {{ state.first_name }} !</h3>
                <p class="text-gray-600">Nous revenons vers toi dans un délai maximum de 2 jours.</p>
              </div>

              <UForm v-else :schema="waitingListSchema" :state="state" class="space-y-6" @submit="onSubmit">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <UFormField label="Nom" name="last_name" required>
                    <UInput v-model="state.last_name" placeholder="Votre nom" class="w-full"/>
                  </UFormField>
                  <UFormField label="Prénom" name="first_name" required>
                    <UInput v-model="state.first_name" placeholder="Votre prénom" class="w-full"/>
                  </UFormField>
                </div>

                <UFormField label="Email" name="email" required>
                  <UInput v-model="state.email" placeholder="votre@email.com" class="w-full"/>
                </UFormField>

                <UFormField label="Date de naissance" name="birth_date" required>
                  <UInput v-model="state.birth_date" type="date" class="w-full"/>
                </UFormField>

                <UFormField label="Tes réseaux sociaux" name="socials">
                  <div class="space-y-2">
                    <div v-for="(_, index) in state.socials" :key="index" class="flex gap-2">
                      <UInput v-model="state.socials[index]" placeholder="https://..." class="flex-1"/>
                      <UButton icon="i-heroicons-trash" color="error" variant="ghost" @click="removeSocial(index)" v-if="state.socials.length > 1"/>
                    </div>
                    <UButton icon="i-heroicons-plus" color="secondary" variant="solid" size="xs" @click="addSocial">
                      Ajouter un lien
                    </UButton>
                  </div>
                </UFormField>

                <UFormField label="Un ou plusieurs liens vers ton travail" name="links">
                  <div class="space-y-2">
                    <div v-for="(_, index) in state.links" :key="index" class="flex gap-2">
                      <UInput v-model="state.links[index]" placeholder="https://..." class="flex-1"/>
                      <UButton icon="i-heroicons-trash" color="error" variant="ghost" @click="removeLink(index)" v-if="state.links.length > 1"/>
                    </div>
                    <UButton icon="i-heroicons-plus" color="secondary" variant="solid" size="xs" @click="addLink">
                      Ajouter un lien
                    </UButton>
                  </div>
                </UFormField>

                <UFormField name="has_work">
                  <UCheckbox v-model="state.has_work" label="J'ai un titre à partager sur Wreadia"/>
                </UFormField>

                <UFormField name="has_work_details" v-show="state.has_work" label="Parle-nous-en en quelques mots">
                  <UTextarea v-model="state.has_work_details" class="w-full" :rows="5"/>
                </UFormField>

                <div class="space-y-4 pt-4 border-t border-gray-200">
                  <UFormField name="optin_tos" required>
                    <UCheckbox v-model="state.optin_tos">
                      <template #label>
                        J’ai lu et j’accepte les <ULink href="/cgu" target="_blank">conditions générales d’utilisation</ULink>.
                      </template>
                    </UCheckbox>
                  </UFormField>

                  <UFormField name="optin_data_processing" required>
                    <UCheckbox
                        v-model="state.optin_data_processing">
                      <template #label>
                        En soumettant ce formulaire, j’accepte que mes informations soient traitées par Wreadia SASU pour l’examen de ma candidature.
                        Je dispose d’un droit d’accès, de rectification, d’effacement, d’opposition et de portabilité de mes données.
                        <ULink href="/confidentialite" target="_blank">Politique de confidentialité</ULink>.
                      </template>
                    </UCheckbox>
                  </UFormField>
                </div>

                <NuxtTurnstile ref="turnstile" v-model="state.token"></NuxtTurnstile>

                <UAlert v-if="error" color="error" variant="solid" :title="error" icon="i-heroicons-exclamation-triangle"/>

                <UButton type="submit" color="primary" variant="solid" block :loading="loading">
                  Envoyer ma candidature
                </UButton>
              </UForm>
            </UCard>
          </div>
        </UContainer>
      </section>

      <USeparator/>

      <section class="m-0 py-8 md:py-16">
        <UContainer>
          <div class="text-center mb-12">
            <h2 class="mb-16">Ce que tu trouveras à Wreadia</h2>
            <div class="flex flex-col gap-8">
              <div class="lg:max-w-1/2 m-auto">
                <h3 class="text-2xl font-medium mb-2">Ton monde, tes clés.</h3>
                <p class="text-lg">À Wreadia, tu disposes de ton monde : un espace qui t’appartient, dont tu gardes les clés et 100 % de tes droits.
                  Tu
                  y publies tes histoires, tu y retrouves tes lectrices et lecteurs, et tu gardes la main sur ton œuvre du début à la fin.</p>
              </div>
              <div class="lg:max-w-1/2 m-auto">
                <h3 class="text-2xl font-medium mb-2">Une communauté d’auteur.ice.s</h3>
                <p class="text-lg">Wreadia accueille les autrices et auteurs animé·e·s par la passion d’écrire des histoires. Pour mettre chaque
                  univers en avant, nous ouvrons nos portes à une communauté d’auteur·ice·s à taille humaine. Nous accueillons les autrices et auteurs
                  de fiction qui abordent leur travail avec sérieux : un texte relu et travaillé. C’est une exigence nécessaire pour les
                  lecteur·ice·s. Tu y mets du temps, du soin, une part de toi. À Wreadia, on respecte ton travail.</p>
              </div>
              <div class="lg:max-w-1/2 m-auto">
                <h3 class="text-2xl font-medium mb-2">La création est humaine.</h3>
                <p class="text-lg">À Wreadia, les histoires sont créées par des humains. Ce que tes lectrices et lecteurs viennent chercher ici, c’est
                  ta voix, ton regard, ton travail. C’est cela que nous défendons.</p>
              </div>
              <div class="lg:max-w-1/2 m-auto">
                <h3 class="text-2xl font-medium mb-2">Autrice et auteur fondateur·ice.</h3>
                <p class="text-lg">Wreadia est en phase alpha. Rejoindre, publier et lire est gratuit pendant cette période. Rejoindre Wreadia pendant
                  cette période te donne le statut d’auteur·ice fondateur·ice.</p>
              </div>
            </div>
          </div>
          <p class="text-center text-4xl font-serif">Bienvenue à Wreadia,<br>le club de la fiction indépendante.</p>
        </UContainer>
      </section>
    </div>
  </UPage>
</template>

<style scoped>

</style>
